import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { ArchiveIcon, ArchiveRestoreIcon, PencilIcon, PlusIcon, WalletIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { sessionQuery } from '#admin/features/auth/queries.ts'
import { KIND_LABEL } from '#admin/features/billing/payment-methods-api.ts'
import { hasPlatformScope } from '#admin/features/platform/api.ts'
import { PROVIDER_LABEL, type CatalogMethod } from '#admin/features/platform/payment-methods/api.ts'
import { MethodDialog } from '#admin/features/platform/payment-methods/method-dialog.tsx'
import {
  useReorderCatalog,
  useUpdateCatalogMethod,
} from '#admin/features/platform/payment-methods/mutations.ts'
import { catalogQuery } from '#admin/features/platform/payment-methods/queries.ts'
import { CatalogSkeleton } from '#admin/features/platform/platform-skeletons.tsx'

/**
 * `/platform/payment-methods` — the payment method catalog for every edition. Staff add, edit,
 * retire and restore methods here. Each edition turns methods on at Billing > Payment methods.
 */
export const Route = createFileRoute('/_authenticated/platform/payment-methods')({
  pendingComponent: CatalogSkeleton,
  staticData: { crumb: 'Payment methods' },
  loader: ({ context }) => context.queryClient.ensureQueryData(catalogQuery()),
  component: PaymentMethodsPage,
})

type Dialog =
  | { kind: 'edit'; editing: CatalogMethod | null }
  | { kind: 'retire'; method: CatalogMethod }

function PaymentMethodsPage() {
  const { data } = useSuspenseQuery(catalogQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const canManage = hasPlatformScope(session, 'platform:payments.manage')
  const [dialog, setDialog] = useState<Dialog | null>(null)
  const close = () => setDialog(null)
  // Only `mutate`, which is stable: new columns rebuild every cell and lose the keyboard focus.
  const { mutate: update, isPending: updating } = useUpdateCatalogMethod()
  const { mutate: reorder, isPending: reordering } = useReorderCatalog()
  const nextSortOrder = Math.max(0, ...data.items.map((method) => method.sortOrder)) + 10

  const columns = useMemo<ColumnDef<CatalogMethod, never>[]>(
    () => [
      {
        id: 'name',
        header: 'Method',
        meta: { primary: true },
        cell: ({ row }) => (
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{row.original.name}</span>
            {row.original.retiredAt === null ? null : (
              <StatusBadge tone="neutral">Retired</StatusBadge>
            )}
          </span>
        ),
      },
      {
        id: 'code',
        header: 'Code',
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.code}</span>,
      },
      {
        id: 'type',
        header: 'Type',
        cell: ({ row }) =>
          row.original.provider === null ? (
            <StatusBadge tone="neutral">Manual</StatusBadge>
          ) : (
            <StatusBadge tone="info">Online: {PROVIDER_LABEL[row.original.provider]}</StatusBadge>
          ),
      },
      {
        id: 'kind',
        header: 'Recorded as',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{KIND_LABEL[row.original.kind]}</span>
        ),
      },
      {
        id: 'default',
        header: 'New editions',
        meta: { hideOnCard: true },
        cell: ({ row }) =>
          row.original.enabledByDefault ? 'On' : <span className="text-muted-foreground">Off</span>,
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const, shrink: true },
              cell: ({ row }: { row: { original: CatalogMethod } }) => {
                const method = row.original
                return (
                  <span className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Edit ${method.name}`}
                      onClick={() => setDialog({ kind: 'edit', editing: method })}
                    >
                      <PencilIcon />
                      Edit
                    </Button>
                    {method.retiredAt === null ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Retire ${method.name}`}
                        onClick={() => setDialog({ kind: 'retire', method })}
                      >
                        <ArchiveIcon />
                        Retire
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Restore ${method.name}`}
                        onClick={() => update({ code: method.code, changes: { retired: false } })}
                      >
                        <ArchiveRestoreIcon />
                        Restore
                      </Button>
                    )}
                  </span>
                )
              },
            } satisfies ColumnDef<CatalogMethod, never>,
          ]
        : []),
    ],
    [canManage, update],
  )

  return (
    <>
      <PageHeader
        title="Payment methods"
        description="Every edition can turn on these methods. A payer selects one of them when a registration is submitted."
        actions={
          canManage ? (
            <Button onClick={() => setDialog({ kind: 'edit', editing: null })}>
              <PlusIcon />
              New payment method
            </Button>
          ) : null
        }
      />
      <DataTable
        caption="The payment method catalog"
        columns={columns}
        data={data.items}
        getRowId={(method) => method.code}
        // Payers see the methods in this order, so dragging a row sets the order.
        reorder={
          canManage
            ? { onReorder: reorder, label: (method) => method.name, disabled: reordering }
            : undefined
        }
        empty={
          <EmptyState
            icon={<WalletIcon />}
            title="No payment methods yet"
            description="Add a method. Then each edition can turn it on."
          />
        }
      />

      {dialog?.kind === 'edit' ? (
        <MethodDialog
          key={dialog.editing?.code ?? 'new'}
          open
          onOpenChange={(open) => (open ? undefined : close())}
          editing={dialog.editing}
          nextSortOrder={nextSortOrder}
        />
      ) : null}

      <ConfirmDialog
        open={dialog?.kind === 'retire'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={dialog?.kind === 'retire' ? `Retire ${dialog.method.name}?` : ''}
        description="Payers cannot select it after this, and no edition can turn it on. Invoices that use it keep it. You can restore it later."
        confirmLabel="Retire"
        destructive
        busy={updating}
        onConfirm={() => {
          if (dialog?.kind === 'retire') {
            update({ code: dialog.method.code, changes: { retired: true } }, { onSuccess: close })
          }
        }}
      />
    </>
  )
}
